import type { OfficialVaultRequest } from 'src/pages/admin/services/officialVaultService';
import type {
  Vault,
  VaultKind,
  VaultRiskBreach,
  VaultRiskBreachPage,
  VaultRiskLimit,
} from 'src/pages/admin/services/vaultService';
import { type ApiErrorInfo, parseApiError } from 'src/pages/admin/utils/decimal-input';

import { ENGINE_ERROR_KEYS } from '../params/helpers';
import {
  CLOSURE_REASON_MAX,
  EVM_ADDRESS,
  MANAGER_ADDRESS_REASON_MAX,
  PAUSE_REASON_MAX,
  RISK_CRITICAL_LIMIT,
  VAULT_STATES,
  VAULTS_NS as NS,
} from './constants';

const EMPTY = '—';
const DECIMAL = /^(\d+)(?:\.(\d*))?$/;

/**
 * Formatting for this page only (review round 1). The engine sends exact 6 dp strings; every figure here is cut on
 * the string, never through a float, and rounds DOWN (PRD §6.6). The shared params/helpers formatValue trims zeros
 * and does not group thousands, which suits the Parameters page and not a money table.
 */
const split = (value: string, dp: number): { int: string; frac: string } | null => {
  const m = DECIMAL.exec(value.trim());
  if (!m) return null;
  const int = m[1].replace(/^0+(?=\d)/, '');
  return { int, frac: (m[2] ?? '').padEnd(dp, '0').slice(0, dp) };
};

const group = (int: string): string => int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const fixed = (value: string, dp: number): string => {
  const parts = split(value, dp);
  if (!parts) return EMPTY;
  return dp > 0 ? `${group(parts.int)}.${parts.frac}` : group(parts.int);
};

/** USDC at 2 dp, rounded down, with thousands separators: "1234567.891234" → "1,234,567.89". */
export const formatUsdc = (value: string): string => fixed(value, 2);

/**
 * A USDC amount as typed or as the ledger holds it, for forms and details rather than money columns: thousands
 * separators, trailing zeros dropped, at most 6 dp, cut and never rounded up. "1000" → "1,000",
 * "10000.000000" → "10,000", "1234.5" → "1,234.5" (PO, 2026-10-01: "1,000 USDC", "10,000 USDC").
 */
export const formatUsdcAmount = (value: string): string => {
  const parts = split(value, 6);
  if (!parts) return EMPTY;
  const frac = parts.frac.replace(/0+$/, '');
  return frac ? `${group(parts.int)}.${frac}` : group(parts.int);
};

/** NAV at 4 dp, rounded down (6 internal, 4 displayed): "1.000000" → "1.0000". */
export const formatNav = (value: string): string => fixed(value, 4);

/** Shares (units) at their full 6 dp, with thousands separators: they are exact ledger figures, never rounded. */
export const formatUnits = (value: string): string => fixed(value, 6);

/** One more in the last place of a digit string: "099" → "100", "99" → "100". */
const increment = (digits: string): string => {
  const out = digits.split('');
  for (let i = out.length - 1; i >= 0; i -= 1) {
    if (out[i] !== '9') {
      out[i] = String(Number(out[i]) + 1);
      return out.join('');
    }
    out[i] = '0';
  }
  return `1${out.join('')}`;
};

/**
 * A signed USDC figure at 2 dp, rounded DOWN toward −∞ like the engine (§6.6, RoundFloor): a loss is never shown
 * smaller than it is. "-0.166667" → "-0.17", "12.349999" → "12.34", "-0.000000" → "0.00". String arithmetic only.
 */
export const formatSignedUsdc = (value: string): string => {
  const v = value.trim();
  if (!v.startsWith('-')) return formatUsdc(v);
  const parts = split(v.slice(1), 6);
  if (!parts) return EMPTY;
  let cents = `${parts.int}${parts.frac.slice(0, 2)}`;
  if (/[1-9]/.test(parts.frac.slice(2))) cents = increment(cents);
  cents = cents.replace(/^0+(?=\d{3})/, '').padStart(3, '0');
  if (!/[1-9]/.test(cents)) return '0.00';
  return `-${group(cents.slice(0, -2))}.${cents.slice(-2)}`;
};

/** −1, 0 or 1 for a decimal string, read without a float. */
export const signOf = (value: string): -1 | 0 | 1 => {
  const v = value.trim();
  if (!/[1-9]/.test(v)) return 0;
  return v.startsWith('-') ? -1 : 1;
};

/** A fraction as a percent at 2 dp, rounded down: "0.499999" → "49.99%". */
export const formatPercent = (fraction: string): string => {
  const parts = split(fraction, 6);
  if (!parts) return EMPTY;
  const digits = `${parts.int}${parts.frac}`; // the fraction × 1e6
  const hundredths = digits.slice(0, -2).replace(/^0+(?=\d)/, '') || '0'; // × 1e4 = percent × 100, rounded down
  const padded = hundredths.padStart(3, '0');
  return `${group(padded.slice(0, -2))}.${padded.slice(-2)}%`;
};

export const formatCount = (n: number): string => group(String(Math.trunc(n)));

/**
 * An instant as `YYYY-MM-DD HH:mm` in the viewer's own timezone (the browser's, unless one is named): every time on the
 * vault console reads in the zone of whoever is looking (XBIT-4620). Finance reconciling against the venue gets the exact
 * UTC value in the filters' ISO form; the column headers say "local time". A malformed instant shows as "—".
 */
export const formatInstant = (iso: string, timeZone?: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return EMPTY;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(d);
  const at = (type: string) => parts.find((x) => x.type === type)?.value ?? '';
  return `${at('year')}-${at('month')}-${at('day')} ${at('hour')}:${at('minute')}`;
};

export interface CapacityCells {
  tvl: string;
  managerPresentValue: string;
  sizeCap: string;
  remaining: string;
  utilization: string;
  nav: string;
  /** TVL has reached the cap: nothing more can come in. */
  overCap: boolean;
}

/**
 * The FR-13 capacity columns of one row (XBIT-4238). Before the vault's first cut-off every cell is "—", never a
 * zero that would read as an empty vault; a zero cap has no utilization.
 */
export const capacityCells = (vault: Vault): CapacityCells => {
  const v = vault.valuation;
  if (!v) {
    return {
      tvl: EMPTY,
      managerPresentValue: EMPTY,
      sizeCap: EMPTY,
      remaining: EMPTY,
      utilization: EMPTY,
      nav: EMPTY,
      overCap: false,
    };
  }
  // The unit is in the column header ("TVL (USDC)"), so a cell carries the number only.
  const usdc = formatUsdc;
  const util = v.utilization;
  const whole = util === null ? null : split(util, 6);
  return {
    tvl: usdc(v.totalAssets),
    managerPresentValue: usdc(v.managerPresentValue),
    sizeCap: usdc(v.sizeCap),
    remaining: usdc(v.remainingCapacity),
    utilization: util === null ? EMPTY : formatPercent(util),
    nav: formatNav(v.nav),
    overCap: whole !== null && whole.int !== '0',
  };
};

/** The "Stake & capacity" block of the §14.4 detail (XBIT-4239): the list's capacity cells, plus the ratio. */
export interface DetailFigures extends CapacityCells {
  /** C-03 managerRatio: the manager's shares ÷ all shares, as a percent; "—" before a cut-off or from an older BFF. */
  managerRatio: string;
  /** The vault's own ownership floor: the immutable term (§14.4 "minimum ownership"). */
  ownershipFloor: string;
  /**
   * The floor the ratio is read against: the one the engine enforces, max(own floor, tier requirement) (C-06
   * effectiveFloor, XBIT-4373); the own floor from a BFF that does not send it yet.
   */
  floor: string;
  /** The tier whose requirement sets `floor` (it is above the own floor), else null. */
  floorTier: string | null;
  /** The ratio sits below `floor` (both 6-dp fractions, compared exactly). */
  belowFloor: boolean;
}

/** `a < b` on two 6-dp fractions, as integers of 1e-6 — never through Number() (convention 12). */
const lessThan6 = (a: string, b: string): boolean => {
  const x = split(a, 6);
  const y = split(b, 6);
  return x !== null && y !== null && BigInt(`${x.int}${x.frac}`) < BigInt(`${y.int}${y.frac}`);
};

export const detailFigures = (vault: Vault): DetailFigures => {
  const v = vault.valuation;
  const ratio = v?.managerRatio ?? null;
  const floor = vault.effectiveFloor ?? vault.ownershipFloor;
  return {
    ...capacityCells(vault),
    managerRatio: ratio === null ? EMPTY : formatPercent(ratio),
    ownershipFloor: formatPercent(vault.ownershipFloor),
    floor: formatPercent(floor),
    floorTier: vault.tier && lessThan6(vault.ownershipFloor, floor) ? vault.tier : null,
    belowFloor: ratio !== null && lessThan6(ratio, floor),
  };
};

/** What the match needs of an application (the official-vaults read the console already has). */
export type ApplicationEnding = Pick<OfficialVaultRequest, 'vaultId' | 'status' | 'review'>;

/**
 * An application that ended without its vault ever going Live: C-08 writes CANCELLED both for a review rejection and for
 * the stake window elapsing, and a recorded rejection can precede that write (`review.outcome = REJECTED`).
 */
const endedWithoutGoingLive = (a: ApplicationEnding): boolean =>
  a.status === 'CANCELLED' || a.review?.outcome === 'REJECTED';

/**
 * Vault Management lists only vaults that went Live (PO, 2026-10-01; QA 2026-10-12). A DRAFT vault is still an
 * application in progress and is followed on Vault Creation & Application. A rejected or cancelled application's draft
 * is closed (CloseIfDraft, so state CLOSED) and must stay off this list too (XBIT-4699): a CLOSED vault whose
 * application ended REJECTED or CANCELLED is hidden. A CLOSED vault whose application went Live (closed after it was
 * open) or that has no application record (seeded vaults, records from before C-08) stays. The engine's `vaults` query
 * keeps returning all of them for the detail page and the review flow, so the filter belongs to this list.
 *
 * `applications` is the whole official-vaults read (GET /vault/official-vaults is not paged: the BFF takes no
 * parameters and the engine lists every key of the onboarding bucket). Without it (the read failed) nothing but DRAFT is
 * hidden: a list that hides real vaults on a read error is worse than one that shows a closed draft.
 */
export const managedVaults = (
  vaults: Vault[],
  applications: readonly ApplicationEnding[] = []
): Vault[] => {
  const neverLive = new Set(applications.filter(endedWithoutGoingLive).map((a) => a.vaultId));
  return vaults.filter((v) => v.state !== 'DRAFT' && !(v.state === 'CLOSED' && neverLive.has(v.vaultId)));
};

/**
 * XBIT-4439 (QA): a vault still settling — ACTIVE with no depositor yet. Approve opens the vault ACTIVE one runner
 * tick before the manager's parked stake becomes its first SUB, so a read at approval shows 0 depositors.
 */
export const isSettling = (v: Pick<Vault, 'state' | 'holderCount'>): boolean =>
  v.state === 'ACTIVE' && v.holderCount === 0;

export type StateColor = 'default' | 'info' | 'success' | 'warning' | 'error';

const STATE_COLOR: Record<string, StateColor> = {
  DRAFT: 'default',
  RAISING: 'info',
  ACTIVE: 'success',
  PAUSED: 'warning',
  CLOSING: 'warning',
  CLOSED: 'default',
};

/** A state added later shows raw, uncoloured — never a crash. */
export const stateChip = (state: string): { labelKey: string | null; color: StateColor } =>
  (VAULT_STATES as readonly string[]).includes(state)
    ? { labelKey: `${NS}.state.${state}`, color: STATE_COLOR[state] }
    : { labelKey: null, color: 'default' };

/** The load banner: one translated line; the raw upstream text is not shown. */
export const listErrorMessage = (info: ApiErrorInfo, translate: (key: string) => string): string =>
  translate((info.code && ENGINE_ERROR_KEYS[info.code]) || `${NS}.loadFailed`);

/**
 * A long id cut in the middle so a row stays one line: "0x6d90fe2b47c18a05e3b9d7f0a2c84165be3097df" →
 * "0x6d90fe2b…be3097df". The cell shows the full value on hover and copies it whole.
 */
export const middle = (value: string, head = 10, tail = 8): string =>
  value.length <= head + tail + 1 ? value : `${value.slice(0, head)}…${value.slice(-tail)}`;

// ---- XBIT-4242: the detail's Operations block (Module PRD §14.4 Operations) ----

/** The control the Operations block offers for a vault's subscriptions. */
export type OperationsControl = 'pause' | 'takeOver' | 'resume';

type PauseView = Pick<Vault, 'state' | 'subscriptionsPausedBy'>;

/** §14.4: the block is for a vault that takes subscriptions or has them paused; never DRAFT, RAISING or CLOSED. */
export const showsOperations = (vault: PauseView): boolean =>
  vault.state === 'ACTIVE' || vault.state === 'PAUSED';

/**
 * ACTIVE offers Pause; an operations pause offers Resume; a manager's pause offers only Take over (f1's plan review:
 * reopening a manager's own pause has no PRD line — §14.4 gives operations "only Pause subscriptions"). A PAUSED vault
 * whose pauser is unknown (an older BFF) offers nothing rather than a guess.
 */
export const operationsControl = (vault: PauseView): OperationsControl | null => {
  if (vault.state === 'ACTIVE') return 'pause';
  if (vault.state !== 'PAUSED') return null;
  if (vault.subscriptionsPausedBy === 'OPERATIONS') return 'resume';
  if (vault.subscriptionsPausedBy === 'MANAGER') return 'takeOver';
  return null;
};

/** The i18n key (under `detail.operations.status.`) for the subscriptions line. */
export const subscriptionsStatusKey = (
  vault: PauseView
): 'open' | 'pausedByManager' | 'pausedByOperations' | 'paused' => {
  if (vault.state !== 'PAUSED') return 'open';
  if (vault.subscriptionsPausedBy === 'MANAGER') return 'pausedByManager';
  if (vault.subscriptionsPausedBy === 'OPERATIONS') return 'pausedByOperations';
  return 'paused';
};

/** The engine's bound, mirrored: 1–500 characters after trimming, counted as code points. */
export const validatePauseReason = (reason: string): string | undefined => {
  const n = Array.from(reason.trim()).length;
  if (n === 0) return `${NS}.detail.operations.errors.required`;
  return n > PAUSE_REASON_MAX ? `${NS}.detail.operations.errors.reasonTooLong` : undefined;
};

/**
 * The engine failures, each in its own words (peer review W-M3): a timeout may still have applied, so it must not read
 * as "try again"; the block re-reads the vault after every attempt either way.
 */
export const OPERATIONS_ENGINE_ERROR_KEYS: Record<string, string> = {
  VAULT_ENGINE_TIMEOUT: 'timeout',
  VAULT_ENGINE_UNAVAILABLE: 'unavailable',
  VAULT_ENGINE_AUTH_FAILED: 'authFailed',
  VAULT_ENGINE_NOT_CONFIGURED: 'notConfigured',
};

/** A refused pause / resume, in the console's words; a refused state is named by its label, as the header shows it. */
export const operationsErrorMessages = (
  error: unknown,
  translate: (key: string, options?: object) => string
): string[] => {
  const info = parseApiError(error);
  const key = (k: string) => `${NS}.detail.operations.errors.${k}`;
  if (info.code === 'VAULT_STATE_INVALID') {
    const state = (error as { body?: { vaultState?: unknown } } | null)?.body?.vaultState;
    if (typeof state !== 'string') return [translate(key('stateInvalid'), { state: EMPTY })];
    const { labelKey } = stateChip(state);
    return [translate(key('stateInvalid'), { state: labelKey ? translate(labelKey) : state })];
  }
  if (info.code === 'VAULT_SETTINGS_INVALID') return [translate(key('settingsInvalid'))];
  if (info.code === 'VAULT_NOT_FOUND') return [translate(key('notFound'))];
  if (info.status === 403) return [translate(key('forbidden'))];
  const engine = info.code ? OPERATIONS_ENGINE_ERROR_KEYS[info.code] : undefined;
  if (engine) return [translate(key(engine))];
  // A bare 504 (an ingress in front of the BFF) may still have applied too.
  return [translate(key(info.status === 504 ? 'timeout' : 'failed'))];
};

// ---- XBIT-4258: the detail's manager address change (Module PRD §14.4 内部管理账号) ----

/**
 * The block is for an OFFICIAL vault that is ACTIVE or PAUSED (the engine refuses every other kind and state, and says so):
 * a community vault's manager is its applicant, and a DRAFT or RAISING vault is still being onboarded.
 */
export const showsManagerAddress = (vault: Pick<Vault, 'kind' | 'state'>): boolean =>
  vault.kind === 'OFFICIAL' && (vault.state === 'ACTIVE' || vault.state === 'PAUSED');

/** The new address's format: 0x + 40 hex, any case. The engine checks the rest (binding, company, the vault's own account). */
export const validateManagerAddress = (address: string): string | undefined =>
  EVM_ADDRESS.test(address.trim())
    ? undefined
    : `${NS}.detail.managerAddress.errors.addressInvalid`;

/** 1–500 characters after trimming, counted as code points. */
export const validateManagerAddressReason = (reason: string): string | undefined => {
  const n = Array.from(reason.trim()).length;
  if (n === 0) return `${NS}.detail.managerAddress.errors.reasonRequired`;
  return n > MANAGER_ADDRESS_REASON_MAX
    ? `${NS}.detail.managerAddress.errors.reasonTooLong`
    : undefined;
};

/**
 * The typed confirmation: the operator types the vault's id, exactly, before the change is sent. A change of the address
 * money is attributed from is not one to make by a stray click.
 */
export const confirmsManagerAddress = (typed: string, vaultId: string): boolean =>
  typed.trim() === vaultId;

/**
 * A refused change, in the console's words and then the BFF's own text verbatim for the coded refusals of this action (the
 * engine's reason for it), so an operator reads exactly what was refused.
 */
export const managerAddressErrorMessages = (
  error: unknown,
  translate: (key: string, options?: object) => string
): string[] => {
  const info = parseApiError(error);
  const body = (
    error as { body?: { message?: unknown; reason?: unknown; vaultState?: unknown } } | null
  )?.body;
  const key = (k: string) => `${NS}.detail.managerAddress.errors.${k}`;
  const verbatim = typeof body?.message === 'string' && body.message ? [body.message] : [];
  switch (info.code) {
    case 'VAULT_NOT_OFFICIAL':
      return [translate(key('notOfficial')), ...verbatim];
    case 'VAULT_TRANSFER_IN_FLIGHT':
      return [translate(key('transferInFlight')), ...verbatim];
    case 'VAULT_MANAGER_ADDRESS_NOT_BOUND':
      return [translate(key('notBound')), ...verbatim];
    case 'VAULT_MANAGER_ADDRESS_NOT_ALLOWED':
      return [
        translate(key(body?.reason === 'vault_account' ? 'ownAccount' : 'companyAddress')),
        ...verbatim,
      ];
    case 'VAULT_STATE_INVALID': {
      const state = body?.vaultState;
      const { labelKey } = stateChip(typeof state === 'string' ? state : '');
      return [
        translate(key('stateInvalid'), {
          state: typeof state === 'string' ? (labelKey ? translate(labelKey) : state) : EMPTY,
        }),
      ];
    }
    case 'VAULT_SETTINGS_INVALID':
      return [translate(key('settingsInvalid')), ...verbatim];
    case 'VAULT_NOT_FOUND':
      return [translate(key('notFound'))];
    default:
      break;
  }
  if (info.status === 403) return [translate(key('forbidden'))];
  const engine = info.code ? OPERATIONS_ENGINE_ERROR_KEYS[info.code] : undefined;
  if (engine) return [translate(key(engine))];
  // A bare 504 (an ingress in front of the BFF) may still have applied too.
  return [translate(key(info.status === 504 ? 'timeout' : 'failed'))];
};

// ---- XBIT-4243: the detail's Close vault block (Module PRD §14.5 关闭金库, D-32) ----

export type ClosureAction = 'request' | 'approve' | 'cancel';

/** Why the Close vault button is disabled: closing is shipped dark, or the console could not read whether it is open. */
export type ClosureDisabledReason = 'dark' | 'unknown';

export interface ClosureActionView {
  action: ClosureAction;
  disabled: ClosureDisabledReason | null;
}

type ClosureVaultView = Pick<Vault, 'state' | 'closure'>;

/**
 * §14.5: the block is for a vault that can be closed (RAISING, ACTIVE or PAUSED — never DRAFT, D-32 cl.2) and for one
 * that has a live closure, CLOSING included. A CLOSED vault has nothing to close.
 */
export const showsClosure = (vault: ClosureVaultView): boolean =>
  vault.state === 'RAISING' ||
  vault.state === 'ACTIVE' ||
  vault.state === 'PAUSED' ||
  vault.state === 'CLOSING' ||
  Boolean(vault.closure);

const sameOperator = (a: string, b: string): boolean =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * What the block offers (C-06, XBIT-4243). With no live closure: Close vault, disabled with its reason while the engine
 * says closing is not open (`available` false) or the console could not read that (null). With one: Cancel while
 * AWAITING_APPROVAL or NOTICE (one operator, the safe direction); Approve while AWAITING_APPROVAL to an operator who is
 * not yet an approver — never guessed when the signed-in operator is unknown. From REQUESTED on nothing is offered
 * (irreversible once executed). The flat precondition is the engine's: a refusal is shown as it comes back.
 */
export const closureActions = (
  vault: ClosureVaultView,
  available: boolean | null,
  operatorId: string | undefined
): ClosureActionView[] => {
  const { closure } = vault;
  if (!closure) {
    if (vault.state !== 'RAISING' && vault.state !== 'ACTIVE' && vault.state !== 'PAUSED')
      return [];
    let disabled: ClosureDisabledReason | null = null;
    if (available === false) disabled = 'dark';
    else if (available === null) disabled = 'unknown';
    return [{ action: 'request', disabled }];
  }
  const out: ClosureActionView[] = [];
  if (
    closure.state === 'AWAITING_APPROVAL' &&
    operatorId &&
    !closure.approvers.some((approver) => sameOperator(approver, operatorId))
  ) {
    out.push({ action: 'approve', disabled: null });
  }
  if (closure.state === 'AWAITING_APPROVAL' || closure.state === 'NOTICE') {
    out.push({ action: 'cancel', disabled: null });
  }
  return out;
};

/** 1–500 characters after trimming, counted as code points (the BFF's bound; the contract names none). */
export const validateClosureReason = (reason: string): string | undefined => {
  const n = Array.from(reason.trim()).length;
  if (n === 0) return `${NS}.detail.closure.errors.required`;
  return n > CLOSURE_REASON_MAX ? `${NS}.detail.closure.errors.reasonTooLong` : undefined;
};

/** A refused request / approve / cancel, in the console's words; the closure is read again after every attempt. */
export const closureErrorMessages = (
  error: unknown,
  translate: (key: string, options?: object) => string
): string[] => {
  const info = parseApiError(error);
  const key = (k: string) => `${NS}.detail.closure.errors.${k}`;
  const body = (error as { body?: Record<string, unknown> } | null)?.body ?? {};
  const state = (value: unknown) => {
    if (typeof value !== 'string') return EMPTY;
    const { labelKey } = stateChip(value);
    return labelKey ? translate(labelKey) : value;
  };
  if (info.code === 'VAULT_CLOSURE_NOT_READY') return [translate(key('notReady'))];
  if (info.code === 'VAULT_CLOSURE_NOT_FLAT') {
    return [
      translate(key('notFlat'), {
        positions: typeof body.positions === 'number' ? body.positions : EMPTY,
        orders: typeof body.openOrders === 'number' ? body.openOrders : EMPTY,
      }),
    ];
  }
  if (info.code === 'VAULT_CLOSURE_VAULT_NOT_CLOSABLE') {
    return [translate(key('notClosable'), { state: state(body.vaultState) })];
  }
  if (info.code === 'VAULT_CLOSURE_IN_PROGRESS') return [translate(key('inProgress'))];
  if (info.code === 'VAULT_CLOSURE_WRONG_STATE') return [translate(key('wrongState'))];
  if (info.code === 'VAULT_CLOSURE_SAME_APPROVER') return [translate(key('sameApprover'))];
  if (info.code === 'VAULT_CLOSURE_BOOK_UNREADABLE') return [translate(key('bookUnreadable'))];
  // MISMATCH: the closure the page held is no longer this vault's live one (the BFF checks it before approve and cancel)
  if (info.code === 'VAULT_CLOSURE_NOT_FOUND' || info.code === 'VAULT_CLOSURE_MISMATCH') {
    return [translate(key('closureGone'))];
  }
  if (info.code === 'VAULT_NOT_FOUND') return [translate(key('notFound'))];
  if (info.status === 403) return [translate(key('forbidden'))];
  const engine = info.code ? OPERATIONS_ENGINE_ERROR_KEYS[info.code] : undefined;
  if (engine) return [translate(key(engine))];
  // A bare 504 (an ingress in front of the BFF) may still have applied too.
  return [translate(key(info.status === 504 ? 'timeout' : 'failed'))];
};

/** One kind's console order as the engine holds it (C-06 VaultDisplayOrder, XBIT-4247). */
export interface KindOrder {
  revision: number;
  orderedVaultIds: string[];
}

export type KindOrders = Partial<Record<VaultKind, KindOrder>>;

const compareAmounts = (a: string, b: string): number => {
  const x = split(a, 6);
  const y = split(b, 6);
  if (!x || !y) return 0;
  if (x.int.length !== y.int.length) return x.int.length - y.int.length;
  if (x.int !== y.int) return x.int < y.int ? -1 : 1;
  if (x.frac !== y.frac) return x.frac < y.frac ? -1 : 1;
  return 0;
};

/**
 * The order the app lists vaults nobody positioned in (vaultread `sortByDisplayOrder`, XBIT-4247): TVL descending, a
 * vault without a priced valuation last, then by vault id. The console's own list is by creation time, so a vault
 * the console shows in that order would change places in the app the moment an operator drags another one (the first
 * drop positions every vault shown). The console has the last cut-off's TVL, the app a live one: the two agree except
 * when a cut-off TVL ranks two vaults the other way round than the live figures do.
 */
export const inAppOrder = (vaults: Vault[]): Vault[] =>
  [...vaults].sort((a, b) => {
    const ta = a.valuation?.totalAssets;
    const tb = b.valuation?.totalAssets;
    const pa = ta !== undefined && split(ta, 6) !== null;
    const pb = tb !== undefined && split(tb, 6) !== null;
    if (pa !== pb) return pa ? -1 : 1;
    if (pa && pb) {
      const byTvl = compareAmounts(tb as string, ta as string);
      if (byTvl !== 0) return byTvl;
    }
    if (a.vaultId === b.vaultId) return 0;
    return a.vaultId < b.vaultId ? -1 : 1;
  });

/**
 * XBIT-4247 (PRD §14.4 Ordering): the list in the order the app lists it. The engine's order puts official first and
 * leaves the rest as it came; within a kind, positioned vaults come first in position order and the unset ones after
 * them in the app's order (inAppOrder) — the DISPLAY_ORDER sort of C-03 §3.5. A position naming a vault the list
 * does not carry (a DRAFT one) is skipped.
 */
export const inConsoleOrder = (vaults: Vault[], orders: KindOrders): Vault[] => {
  const arranged = (kind: VaultKind): Vault[] => {
    const ofKind = vaults.filter((v) => v.kind === kind);
    const byId = new Map(ofKind.map((v) => [v.vaultId, v]));
    const positioned = (orders[kind]?.orderedVaultIds ?? [])
      .map((id) => byId.get(id))
      .filter((v): v is Vault => v !== undefined);
    const placed = new Set(positioned.map((v) => v.vaultId));
    return [...positioned, ...inAppOrder(ofKind.filter((v) => !placed.has(v.vaultId)))];
  };
  const other = vaults.filter((v) => v.kind !== 'OFFICIAL' && v.kind !== 'COMMUNITY');
  return [...arranged('OFFICIAL'), ...arranged('COMMUNITY'), ...other];
};

/**
 * The list a drop asks the engine for: `fromId` moved to `toId`'s place among the vaults of one kind, as the whole
 * list of that kind — every vault shown, in its new place. The positioned vaults the list does not show (a DRAFT one)
 * follow, so the write does not unset them silently. Null when nothing moves: the same vault, an unknown id, or two
 * vaults of different kinds (official and community are ordered separately).
 */
export const reorderedIds = (
  shown: Vault[],
  orders: KindOrders,
  fromId: string,
  toId: string
): { kind: VaultKind; orderedVaultIds: string[] } | null => {
  const from = shown.find((v) => v.vaultId === fromId);
  const to = shown.find((v) => v.vaultId === toId);
  if (!from || !to || from.vaultId === to.vaultId || from.kind !== to.kind) return null;
  // The vaults nobody positioned go in the app's order, so the first drop keeps what the app shows for them.
  const ids = inConsoleOrder(
    shown.filter((v) => v.kind === from.kind),
    orders
  ).map((v) => v.vaultId);
  const hidden = (orders[from.kind]?.orderedVaultIds ?? []).filter((id) => !ids.includes(id));
  const newIndex = ids.indexOf(toId);
  ids.splice(ids.indexOf(fromId), 1);
  ids.splice(newIndex, 0, fromId);
  return { kind: from.kind, orderedVaultIds: [...ids, ...hidden] };
};

/** A refused or failed reorder, in the console's words. */
export const orderErrorMessage = (
  error: unknown,
  translate: (key: string, options?: object) => string
): string => {
  const info = parseApiError(error);
  const key = (k: string) => `${NS}.order.errors.${k}`;
  if (info.code === 'VAULT_ORDER_CONFLICT') return translate(key('conflict'));
  if (info.code === 'VAULT_ORDER_INVALID') return translate(key('invalid'));
  if (info.status === 403) return translate(key('forbidden'));
  const engine = info.code ? OPERATIONS_ENGINE_ERROR_KEYS[info.code] : undefined;
  if (engine) return translate(key(engine));
  return translate(key(info.status === 504 ? 'timeout' : 'failed'));
};

/** What the header badge shows (XBIT-4764): nothing, the grey "unavailable", amber, or red. */
export type RiskBadge =
  | { kind: 'hidden' }
  | { kind: 'unavailable' }
  | { kind: 'warning' | 'critical'; count: number };

/**
 * The badge from the OPEN-only read (a page of the five limits, which holds every open episode: one per vault and
 * limit). Red when the FORCED_DELEVERAGE episode is open, amber for any other open one, hidden at 0. A failed read is
 * "unavailable", never 0 and never hidden: an operator must not read an unreadable monitor as a clean vault. While the
 * first read is still out nothing is shown, so the badge never flashes.
 */
export const riskBadge = (
  open: VaultRiskBreachPage | null,
  failed: boolean
): RiskBadge => {
  if (failed) return { kind: 'unavailable' };
  if (!open || open.total <= 0) return { kind: 'hidden' };
  const critical = open.items.some((item) => item.limit === RISK_CRITICAL_LIMIT);
  return { kind: critical ? 'critical' : 'warning', count: open.total };
};

/** Limits whose measured value is a multiple (effective leverage); the others are fractions of equity. */
const LEVERAGE_LIMITS: readonly VaultRiskLimit[] = ['MAX_LEVERAGE'];

/**
 * A breach's measured value or the limit in force, cut on the string and rounded down like every figure on this
 * page: leverage as "3.12×", a fraction as a percent ("52.30%"). INSTRUMENT_WHITELIST has no number (its value is a
 * flag, and the coins are the detail) and a missing limit has none: both "—".
 */
export const formatBreachFigure = (limit: VaultRiskLimit, value: string | null): string => {
  if (value == null || limit === 'INSTRUMENT_WHITELIST') return EMPTY;
  if (LEVERAGE_LIMITS.includes(limit)) {
    const parts = split(value, 2);
    return parts ? `${group(parts.int)}.${parts.frac}×` : EMPTY;
  }
  return formatPercent(value);
};

/** An episode's status cell: open while closedAt is null. */
export const isOpenBreach = (breach: Pick<VaultRiskBreach, 'closedAt'>): boolean =>
  breach.closedAt == null;
